/**
 * Step 6 — Scope of Work. AI drafts it when enabled + consented; otherwise the tech writes it per area.
 */
import { useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Brain, Loader2, Sparkles, Edit2, Check } from "lucide-react";
import { toast } from "sonner";
import { useAI } from "@/lib/ai";
import { AIResultNote } from "@/components/AIResultNote";

const SEVERITIES = ["Minor", "Moderate", "Severe", "Hazardous"];

const SEVERITY_COLORS = {
  Minor: "bg-emerald-100 text-emerald-700",
  Moderate: "bg-amber-100 text-amber-800",
  Severe: "bg-rose-100 text-rose-700",
  Hazardous: "bg-purple-100 text-purple-700",
};

export default function Step6Scope({ workflow, requestId, onAdvance }) {
  const { aiOn } = useAI();
  const step6 = workflow?.step_6 || {};
  const [report, setReport] = useState(step6.ai_report || null);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null); // area index
  const [saving, setSaving] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await API.post(`/inspection/${requestId}/step/6/generate`);
      setReport(data.report);
      toast.success("AI scope-of-work generated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI failed");
    } finally { setGenerating(false); }
  };

  const startManual = () => {
    const areas = workflow?.step_4?.affected_areas || [];
    setReport({ areas: areas.map(area => ({ area, damage_description: "", recommended_scope_of_work: "", severity: "" })),
                overall_notes: "" });
    setEditing(0);
  };

  const updateArea = (idx, patch) => {
    setReport(prev => {
      const next = { ...prev };
      next.areas = [...(prev?.areas || [])];
      next.areas[idx] = { ...next.areas[idx], ...patch };
      return next;
    });
  };

  const accept = async () => {
    if (!report) return;
    setSaving(true);
    try {
      await API.post(`/inspection/${requestId}/step/6/accept`, { ai_report: report });
      toast.success("Scope of work accepted");
      onAdvance(7);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="step-6-scope">
      <div className="flex items-center gap-2">
        <Brain className="w-5 h-5 text-amber-600" />
        <h2 className="text-base font-bold text-zinc-900">Step 6 · {aiOn ? "AI Scope of Work" : "Scope of Work"}</h2>
      </div>

      {!report && !aiOn && (
        <Card><CardContent className="pt-6 pb-6 text-center">
          <p className="text-sm text-zinc-700 font-semibold">Write the Scope of Work</p>
          <p className="text-xs text-zinc-500 mt-1 mb-4">
            Describe the damage, severity and recommended work for each affected area from Step 4.
          </p>
          <Button onClick={startManual} className="h-12 bg-zinc-900 hover:bg-zinc-800 text-white"
                  data-testid="step-6-start-manual">Start Scope of Work</Button>
        </CardContent></Card>
      )}

      {!report && aiOn && (
        <Card><CardContent className="pt-6 pb-6 text-center">
          <Sparkles className="w-10 h-10 text-amber-400 mx-auto mb-2" />
          <p className="text-sm text-zinc-700 font-semibold">Generate AI-Assisted Scope</p>
          <p className="text-xs text-zinc-500 mt-1 mb-4">
            Claude will analyze all accepted photos and propose a per-area scope of work, severity rating, materials, and dry/remove decisions.
          </p>
          <Button onClick={generate} disabled={generating}
                  className="h-12 bg-zinc-900 hover:bg-zinc-800 text-white"
                  data-testid="step-6-generate">
            {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : "Generate Scope of Work"}
          </Button>
        </CardContent></Card>
      )}

      {report && aiOn && (step6.generated_at || step6.ai_generated || step6.ai_report) && editing === null && (
        <AIResultNote feature="scope" refId={requestId} text={JSON.stringify(report)} />
      )}
      {report && (report.areas || []).map((a, idx) => (
        <Card key={idx} data-testid={`step-6-area-card-${idx}`}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-zinc-900">{a.area || `Area ${idx + 1}`}</p>
              {editing === idx ? (
                <select value={a.severity || ""} onChange={e => updateArea(idx, { severity: e.target.value })}
                        className="text-xs border border-zinc-200 rounded px-2 py-1" data-testid={`step-6-severity-${idx}`}>
                  <option value="">Severity…</option>
                  {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <Badge className={SEVERITY_COLORS[a.severity] || "bg-zinc-100 text-zinc-700"}>
                  {a.severity || "Unrated"}
                </Badge>
              )}
            </div>

            <Field label="Damage Description" value={a.damage_description} editing={editing === idx}
                   onChange={v => updateArea(idx, { damage_description: v })} />
            <Field label="Recommended Scope of Work" value={a.recommended_scope_of_work} editing={editing === idx}
                   onChange={v => updateArea(idx, { recommended_scope_of_work: v })} />

            {Array.isArray(a.material_types_affected) && (
              <div className="mt-3">
                <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">Materials</p>
                <div className="flex flex-wrap gap-1">
                  {a.material_types_affected.map((m, i) => <Badge key={i} variant="outline" className="text-[10px]">{m}</Badge>)}
                </div>
              </div>
            )}

            {Array.isArray(a.recommended_services) && (
              <div className="mt-3">
                <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">Recommended Services</p>
                <ul className="list-disc list-inside text-xs text-zinc-700 space-y-0.5">
                  {a.recommended_services.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}

            {Array.isArray(a.dry_or_remove) && (
              <div className="mt-3">
                <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">Dry / Remove</p>
                <div className="space-y-1">
                  {a.dry_or_remove.map((dr, i) => (
                    <div key={i} className="flex justify-between text-xs">
                      <span className="text-zinc-700">{dr.material}</span>
                      <Badge variant="outline" className={`text-[10px] ${
                        dr.action === "Can Be Dried" ? "border-emerald-300 text-emerald-700"
                        : dr.action === "Requires Removal" ? "border-rose-300 text-rose-700"
                        : "border-zinc-300 text-zinc-600"}`}>{dr.action}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button variant="ghost" size="sm" className="mt-3" onClick={() => setEditing(editing === idx ? null : idx)}
                    data-testid={`step-6-toggle-edit-${idx}`}>
              <Edit2 className="w-3 h-3 mr-1" /> {editing === idx ? "Done editing" : "Edit"}
            </Button>
          </CardContent>
        </Card>
      ))}

      {report && (
        <>
          <Card><CardContent className="pt-4">
            <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">Overall Notes</p>
            <textarea
              value={report.overall_notes || ""}
              onChange={e => setReport(prev => ({ ...prev, overall_notes: e.target.value }))}
              className="w-full p-2 text-xs border border-zinc-200 rounded h-24"
              data-testid="step-6-overall-notes"
            />
          </CardContent></Card>

          <Button onClick={accept} disabled={saving}
                  className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="step-6-accept">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> :
              <><Check className="w-4 h-4 mr-1" /> Accept Scope & Continue</>}
          </Button>
          {aiOn && (
            <Button onClick={generate} variant="outline" disabled={generating}
                    className="w-full h-10" data-testid="step-6-regenerate">
              Regenerate
            </Button>
          )}
        </>
      )}
    </div>
  );
}

function Field({ label, value, editing, onChange }) {
  return (
    <div className="mt-3">
      <p className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">{label}</p>
      {editing ? (
        <textarea value={value || ""} onChange={e => onChange(e.target.value)}
                  className="w-full p-2 text-xs border border-zinc-200 rounded h-20" />
      ) : (
        <p className="text-xs text-zinc-700 leading-relaxed">{value || <em className="text-zinc-400">—</em>}</p>
      )}
    </div>
  );
}
